import { useEffect, useState } from "react";
import { questions, ruleCount, topicLabels, type Question } from "./data/questions";
import { beginSession, clearProgress, loadProgress, recordAnswer, type ProgressData } from "./lib/progress";
import { prepareQuestions, selectQuestions, type PreparedQuestion } from "./lib/quiz";
import { initialOfflineStatus, OFFLINE_STATUS_EVENT, type OfflineStatus } from "./lib/offline";

type Mode = "exam" | "training";
type Screen = "home" | "quiz" | "result";
interface Answer { selected: number; correct: boolean }
interface InstallPrompt extends Event { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> }

const counts = [10, 30, 50, 100];
const letters = ["A", "B", "C", "D"];

function Icon({ name }: { name: "book" | "exam" | "arrow" | "help" | "check" | "plane" | "download" }) {
  const paths = {
    book: <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H11v15H6.5A2.5 2.5 0 0 0 4 20.5Zm16 0A2.5 2.5 0 0 0 17.5 3H13v15h4.5a2.5 2.5 0 0 1 2.5 2.5Z" />,
    exam: <><path d="M9 5h10v16H5V5h2"/><path d="M9 3h6v4H9zM8 12l2 2 4-4M8 18h7"/></>,
    arrow: <path d="m9 18 6-6-6-6" />,
    help: <><circle cx="12" cy="12" r="9"/><path d="M9.7 9a2.4 2.4 0 1 1 3.2 2.3c-.9.4-.9 1.1-.9 1.7M12 17h.01"/></>,
    check: <path d="m5 12 4 4L19 6" />,
    plane: <path d="m3 13 18-8-7.5 8.5-1.5 7-3-5-4 1.5 2-3Z" />,
    download: <><path d="M12 3v12m-4-4 4 4 4-4M5 20h14"/></>,
  };
  return <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name]}</svg>;
}

function ManualDialog({ question, close, mode }: { question: Question; close: () => void; mode: Mode }) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && close();
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, [close]);
  return <div className="dialog-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && close()}>
    <section className="dialog" role="dialog" aria-modal="true" aria-labelledby="manual-title">
      <div className="dialog-grab" />
      <button className="dialog-close" onClick={close} aria-label="Close">×</button>
      <span className="eyebrow">OM-A · SECTION 8</span>
      <h2 id="manual-title">Explanation</h2>
      <div className="manual-ref"><span>Section {question.section}</span><span>Page {question.manualPage}</span></div>
      <div className="explanation-card">
        <p>{question.explanation}</p>
        {question.sourceQuote !== question.explanation && <blockquote>{question.sourceQuote}</blockquote>}
      </div>
      <button className="primary back-to-session" onClick={close}>BACK TO {mode === "exam" ? "EXAM" : "TRAINING"}</button>
      <p className="fine-print">Training aid only. Always use the current controlled OM-A, OM-B, OM-C, QRH and FCOM for flight operations.</p>
    </section>
  </div>;
}

export default function App() {
  const [screen, setScreen] = useState<Screen>("home");
  const [mode, setMode] = useState<Mode>("exam");
  const [count, setCount] = useState(30);
  const [progress, setProgress] = useState<ProgressData>(() => loadProgress(questions.map((question) => question.id)));
  const [session, setSession] = useState<PreparedQuestion[]>([]);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState<Record<number, Answer>>({});
  const [manual, setManual] = useState<Question | null>(null);
  const [installPrompt, setInstallPrompt] = useState<InstallPrompt | null>(null);
  const [offlineStatus, setOfflineStatus] = useState<OfflineStatus>(initialOfflineStatus);

  useEffect(() => {
    const handler = (event: Event) => { event.preventDefault(); setInstallPrompt(event as InstallPrompt); };
    addEventListener("beforeinstallprompt", handler);
    return () => removeEventListener("beforeinstallprompt", handler);
  }, []);

  useEffect(() => {
    const handleStatus = (event: Event) => setOfflineStatus((event as CustomEvent<OfflineStatus>).detail);
    addEventListener(OFFLINE_STATUS_EVENT, handleStatus);
    if (!navigator.onLine && navigator.serviceWorker?.controller) setOfflineStatus("ready");
    return () => removeEventListener(OFFLINE_STATUS_EVENT, handleStatus);
  }, []);

  const selected = answers[current];
  const currentItem = session[current];

  function start() {
    const selectedQuestions = selectQuestions(questions, count, progress);
    const nextProgress = beginSession(progress, selectedQuestions.map((item) => item.id));
    setProgress(nextProgress);
    setSession(prepareQuestions(selectedQuestions));
    setAnswers({});
    setCurrent(0);
    setScreen("quiz");
    scrollTo({ top: 0, behavior: "instant" });
  }

  function choose(sourceIndex: number) {
    if (selected || !currentItem) return;
    const correct = sourceIndex === currentItem.question.correctIndex;
    setAnswers((value) => ({ ...value, [current]: { selected: sourceIndex, correct } }));
    setProgress((value) => recordAnswer(value, currentItem.question, correct));
  }

  function next() {
    if (!selected) return;
    if (current === session.length - 1) {
      setScreen("result");
      scrollTo({ top: 0, behavior: "smooth" });
    } else {
      setCurrent((value) => value + 1);
      scrollTo({ top: 0, behavior: "smooth" });
    }
  }

  function goHome() {
    setScreen("home"); setSession([]); setAnswers({}); setCurrent(0);
    scrollTo({ top: 0, behavior: "smooth" });
  }

  const resultCorrect = Object.values(answers).filter((item) => item.correct).length;
  const percent = session.length ? Math.round((resultCorrect / session.length) * 100) : 0;
  const errors = session.map((item, index) => ({ item, answer: answers[index] })).filter(({ answer }) => answer && !answer.correct);

  return <div className="app-shell">
    {screen === "home" && <>
      <header className="hero">
        <div className="hero-image" aria-hidden="true" />
        <div className="hero-shade" />
        <nav className="brand"><img src="/brand-mark.svg" alt="" /><div><strong>SILK WAY WEST</strong><span>FLIGHT TRAINING</span></div></nav>
        <div className="hero-copy">
          <span className="eyebrow light">OM-A · SECTION 8</span>
          <h1>Know the procedures.<br />Fly with confidence.</h1>
          <p>{ruleCount} independent OM-A rules · {questions.length} professional questions.</p>
        </div>
      </header>
      <main className="home-content">
        <section className="section-block">
          <div className="section-title"><span>01</span><h2>Select mode</h2></div>
          <div className="mode-grid">
            <button className={`mode-card ${mode === "exam" ? "active" : ""}`} onClick={() => setMode("exam")}>
              <span className="mode-icon"><Icon name="exam" /></span><strong>Exam</strong><small>Results and incorrect answers are shown only after completion</small><i>{mode === "exam" && <Icon name="check" />}</i>
            </button>
            <button className={`mode-card ${mode === "training" ? "active" : ""}`} onClick={() => setMode("training")}>
              <span className="mode-icon"><Icon name="book" /></span><strong>Training</strong><small>Immediate feedback and manual explanation after each answer</small><i>{mode === "training" && <Icon name="check" />}</i>
            </button>
          </div>
        </section>

        <section className="section-block">
          <div className="section-title"><span>02</span><h2>Number of questions</h2></div>
          <div className="count-grid">{counts.map((value) => <button key={value} className={count === value ? "active" : ""} onClick={() => setCount(value)}>{value}</button>)}</div>
          <p className="adaptive-note"><span>ADAPTIVE</span> New questions are prioritised. Incorrect answers return more often until answered correctly three times in a row.</p>
        </section>

        <div className={`offline-status ${offlineStatus}`} role="status" aria-live="polite">
          <span className="offline-status-icon"><Icon name={offlineStatus === "ready" ? "check" : "download"} /></span>
          <div>
            <strong>{offlineStatus === "ready" ? "Available offline" : offlineStatus === "preparing" ? "Preparing offline access" : offlineStatus === "unsupported" ? "Offline mode unavailable" : "Offline download failed"}</strong>
            <small>{offlineStatus === "ready" ? "The complete training app is saved on this device." : offlineStatus === "preparing" ? "Keep the app open online until this turns green." : offlineStatus === "unsupported" ? "Open this page in a browser that supports installed web apps." : "Reconnect, close the app and open it once more."}</small>
          </div>
        </div>

        <button className="primary start-button" onClick={start}>Start {mode === "exam" ? "exam" : "training"}<Icon name="arrow" /></button>
        {installPrompt && <button className="install-button" onClick={async () => { await installPrompt.prompt(); await installPrompt.userChoice; setInstallPrompt(null); }}><Icon name="download" /> Install on this device</button>}
        <div className="source-link"><a href="/manual/section-8.pdf" target="_blank" rel="noreferrer"><Icon name="book" /> Open OM-A Section 8</a></div>
        {Object.keys(progress.stats).length > 0 && <button className="reset" onClick={() => confirm("Reset all training progress?") && setProgress(clearProgress())}>Reset progress</button>}
      </main>
    </>}

    {screen === "quiz" && currentItem && <main className="quiz-screen">
      <div className="quiz-top"><button onClick={() => confirm("End the current session?") && goHome()} aria-label="Close">×</button><span>{mode === "exam" ? "EXAM" : "TRAINING"}</span><b>{String(current + 1).padStart(2, "0")} / {session.length}</b></div>
      <div className="progress-track"><i style={{ width: `${((current + (selected ? 1 : 0)) / session.length) * 100}%` }} /></div>
      <section className="question-area">
        <div className="topic-pill"><Icon name="plane" /> {topicLabels[currentItem.question.topic]}</div>
        <h1>{currentItem.question.prompt}</h1>
        <div className="options">{currentItem.options.map((option, index) => {
          const isSelected = selected?.selected === option.sourceIndex;
          const isCorrect = option.sourceIndex === currentItem.question.correctIndex;
          const feedback = mode === "training" && selected;
          return <button key={`${option.sourceIndex}-${option.text}`} className={`${isSelected ? "selected" : ""} ${feedback && isCorrect ? "correct" : ""} ${feedback && isSelected && !isCorrect ? "wrong" : ""}`} onClick={() => choose(option.sourceIndex)} disabled={!!selected}>
            <span>{letters[index]}</span><strong>{option.text}</strong>{feedback && isCorrect && <Icon name="check" />}
          </button>;
        })}</div>
        {mode === "training" && selected && <aside className={`feedback ${selected.correct ? "correct" : "wrong"}`}>
          <div><b>{selected.correct ? "Correct" : "Incorrect"}</b><button onClick={() => setManual(currentItem.question)} aria-label="Open explanation"><Icon name="help" /></button></div>
          <p>{currentItem.question.explanation}</p>
        </aside>}
      </section>
      <footer className="quiz-footer"><button className="primary" disabled={!selected} onClick={next}>{current === session.length - 1 ? "Finish" : "Next question"}<Icon name="arrow" /></button></footer>
    </main>}

    {screen === "result" && <main className="result-screen">
      <div className="result-hero"><span className="eyebrow light">SESSION COMPLETE</span><h1>{percent >= 90 ? "Excellent work" : percent >= 75 ? "Good result" : "Review required"}</h1><div className="score-ring" style={{ "--score": `${percent * 3.6}deg` } as React.CSSProperties}><div><strong>{percent}%</strong><span>{resultCorrect} / {session.length}</span></div></div></div>
      <section className="result-body">
        <div className="result-stats"><div><strong>{resultCorrect}</strong><span>correct</span></div><div className="bad"><strong>{errors.length}</strong><span>incorrect</span></div></div>
        {errors.length > 0 ? <><div className="section-title"><span>!</span><h2>Incorrect answers</h2></div><div className="error-list">{errors.map(({ item, answer }, number) => <article key={item.question.id}>
          <div className="error-head"><span>{number + 1}</span><small>{topicLabels[item.question.topic]}</small><button onClick={() => setManual(item.question)} aria-label="Open explanation"><Icon name="help" /></button></div>
          <h3>{item.question.prompt}</h3>
          <p className="your-answer"><b>Your answer:</b> {item.question.choices[answer.selected]}</p>
          <p className="right-answer"><b>Correct answer:</b> {item.question.choices[item.question.correctIndex]}</p>
        </article>)}</div></> : <div className="perfect"><Icon name="check" /><h2>No errors</h2><p>Every answer was correct.</p></div>}
        <button className="primary start-button" onClick={start}>New session <Icon name="arrow" /></button>
        <button className="secondary" onClick={goHome}>Home</button>
      </section>
    </main>}
    {manual && <ManualDialog question={manual} close={() => setManual(null)} mode={mode} />}
  </div>;
}
